import { searchDimmingOpacity } from '../calendarSearchModel'
import { useCalendarSearchHighlight } from '../CalendarSearchHighlightContext'
import { useState, useEffect, useRef, useCallback } from "react"
import { useDraggable, useDndMonitor } from "@dnd-kit/core"
import { Popover } from "antd"
import dayjs from "dayjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTriangleExclamation, faSnowflake } from "@fortawesome/pro-solid-svg-icons"
import { Tooltip } from "antd"
import { DateRangeViewEvent } from "../scheduleApi"
import { EventDisplayInfo, isCrewEditable, isEventDragDisabled } from "../scheduleModel"
import { RESCHEDULED_EVENT_STRIPE_PATTERN, HOVER_POPOVER_OVERLAY_STYLE } from "../scheduleEventStyles"
import EventStatusIcon from "../EventStatusIcon"
import { getEventCardColors } from "../scheduleEventColorsModel"
import { EventColumn, getEventTopOffset, getEventHeight, HOUR_HEIGHT, MIN_EVENT_HEIGHT, snapTo15Minutes } from "./weekViewModel"
import { ELLIPSIS_TEXT_STYLE, CARD_COMPACT_MAX_HEIGHT, getCardContainerStyle } from "./weekViewCardStyles"
import WeekViewCardResizeHandle from "./WeekViewCardResizeHandle"
import AvatarGroup from "../DayView/AvatarGroup"
import EventSelectCrew from "../EventSelectCrew"
import EventPopoverContent from "../EventPopoverContent"

export type Props = {
  eventColumn: EventColumn
  displayInfo?: EventDisplayInfo
  onEventClick: (event: DateRangeViewEvent) => void
  onResize?: (event: DateRangeViewEvent, newDurationMinutes: number) => void
  onResizingChange?: (isResizing: boolean) => void
  topOffset: number
}

const WeekViewEvent = ({ eventColumn, displayInfo, onEventClick, onResize, onResizingChange, topOffset }: Props) => {
  const { searchTerm: highlightTerm } = useCalendarSearchHighlight()
  const [elementWidth, setElementWidth] = useState<number>(0)
  const showAvatars = elementWidth >= 150
  const { event, column, totalColumns } = eventColumn
  const { attributes, listeners, setNodeRef: setDraggableRef, isDragging, transform } = useDraggable({
    id: `event-${event.id}`,
    data: { event },
    disabled: isEventDragDisabled(event, displayInfo),
  })

  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const setNodeRef = useCallback((node: HTMLDivElement | null) => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    if (node) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (entry) {
          setElementWidth(entry.contentRect.width)
        }
      })
      resizeObserverRef.current.observe(node)
      setElementWidth(node.offsetWidth)
    }

    setDraggableRef(node)
  }, [setDraggableRef])

  const resizeId = `event-${event.id}-resize`
  const {
    attributes: resizeAttributes,
    listeners: resizeListeners,
    setNodeRef: setResizeNodeRef,
    isDragging: isResizing,
    transform: resizeTransform,
  } = useDraggable({
    id: resizeId,
    data: { event, isResize: true },
    disabled: isEventDragDisabled(event, displayInfo),
  })

  const [isHovering, setIsHovering] = useState(false)
  const lastResizeTransformY = useRef<number>(0)

  const startTime = dayjs(event.start)
  const endTime = dayjs(event.end)
  const originalDurationMinutes = endTime.diff(startTime, 'minutes')

  const resizeDelta = resizeTransform?.y ?? 0

  useEffect(() => {
    if (resizeTransform?.y !== undefined) {
      lastResizeTransformY.current = resizeTransform.y
    }
  }, [resizeTransform?.y])

  useEffect(() => {
    onResizingChange?.(isResizing)
  }, [isResizing, onResizingChange])

  useDndMonitor({
    onDragEnd: (dragEvent) => {
      if (dragEvent.active.id !== resizeId) {
        return
      }
      const deltaMinutes = (lastResizeTransformY.current / HOUR_HEIGHT) * 60
      const snappedDeltaMinutes = snapTo15Minutes(deltaMinutes)
      const newDuration = Math.max(15, originalDurationMinutes + snappedDeltaMinutes)
      if (onResize && newDuration !== originalDurationMinutes) {
        onResize(event, newDuration)
      }
      lastResizeTransformY.current = 0
    },
  })

  const formatTimeRange = () => {
    if (isResizing) {
      const deltaMinutes = (resizeDelta / HOUR_HEIGHT) * 60
      const snappedDeltaMinutes = snapTo15Minutes(deltaMinutes)
      const newDuration = Math.max(15, originalDurationMinutes + snappedDeltaMinutes)
      const newEndTime = startTime.add(newDuration, 'minute')
      return `${startTime.format('h:mm A')} - ${newEndTime.format('h:mm A')}`
    }
    return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`
  }

  const { backgroundColor, palette } = getEventCardColors(event.crewForemanColor)

  const frozenIcon = event.frozen ? (
    <Tooltip title="Frozen">
      <FontAwesomeIcon icon={faSnowflake} style={{ color: palette.secondaryText, fontSize: 11, flexShrink: 0 }} />
    </Tooltip>
  ) : null

  const top = getEventTopOffset(event) - topOffset
  const baseHeight = getEventHeight(event)

  const snappedResizeDelta = isResizing
    ? (snapTo15Minutes((resizeDelta / HOUR_HEIGHT) * 60) / 60) * HOUR_HEIGHT
    : 0
  const height = Math.max(MIN_EVENT_HEIGHT, baseHeight + snappedResizeDelta)

  const dragProps = isResizing ? {} : { ...listeners, ...attributes }

  const widthPercent = totalColumns > 0 ? 100 / totalColumns : 100
  const leftPercent = totalColumns > 0 ? column * widthPercent : 0

  const isCompact = baseHeight < CARD_COMPACT_MAX_HEIGHT
  const isSemiCompact = !isCompact && baseHeight < 75

  const isRescheduled = event.rescheduledToEventId != null

  const style: React.CSSProperties = {
    ...getCardContainerStyle({ top, height, widthPercent, leftPercent, isCompact, isDragging, isResizing, transform }),
    backgroundColor,
    color: palette.primaryText,
    backgroundImage: isRescheduled ? RESCHEDULED_EVENT_STRIPE_PATTERN : undefined,
  }

  const avatarNode = isCrewEditable(displayInfo)
    ? <EventSelectCrew event={event} variant="avatar" color={palette.primaryText} />
    : <AvatarGroup crewForemanEmail={event.crewForemanEmail} crewMemberIds={event.crewMemberIds} initialsColor={palette.primaryText} />

  return (
    <Popover
      content={
        <EventPopoverContent
          event={event}
          workareaStatuses={displayInfo?.workareaStatuses}
          timesheets={displayInfo?.timesheets}
          warning={displayInfo?.warning ?? undefined}
          rescheduledToDate={displayInfo?.rescheduledToDate}
        />
      }
      open={isHovering && !isDragging && !isResizing}
      placement="right"
      overlayStyle={HOVER_POPOVER_OVERLAY_STYLE}
    >
      <div
        ref={setNodeRef}
        {...dragProps}
        onClick={(e) => {
          e.stopPropagation()
          if (!isResizing) {
            onEventClick(event)
          }
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => {
          if (!isResizing) {
            setIsHovering(false)
          }
        }}
        style={{ ...style, opacity: searchDimmingOpacity(event, highlightTerm) }}
      >
        {isCompact ? (
          <>
            {displayInfo && showAvatars && (
              <EventStatusIcon status={displayInfo.status} />
            )}
            {displayInfo?.warning && (
              <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 11, flexShrink: 0 }} />
            )}
            {frozenIcon}
            <span
              style={{
                ...ELLIPSIS_TEXT_STYLE,
                fontWeight: 500,
                fontSize: 11,
                flex: 1,
                minWidth: 0,
                textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
              }}
            >
              {[event.customer?.displayName, event.jobName].filter(Boolean).join(' · ')}
            </span>
            {showAvatars && (
              <div style={{ flexShrink: 0 }}>
                {avatarNode}
              </div>
            )}
          </>
        ) : isSemiCompact ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {displayInfo && showAvatars && (
                <EventStatusIcon status={displayInfo.status} />
              )}
              {displayInfo?.warning && (
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 12, flexShrink: 0 }} />
              )}
              {frozenIcon}
              <span
                style={{
                  ...ELLIPSIS_TEXT_STYLE,
                  fontWeight: 500,
                  fontSize: 12,
                  flex: 1,
                  minWidth: 0,
                  textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
                }}
              >
                {[event.customer?.displayName, event.jobName].filter(Boolean).join(' · ')}
              </span>
              {showAvatars && (
                <div style={{ flexShrink: 0 }}>
                  {avatarNode}
                </div>
              )}
            </div>
            <span style={{ ...ELLIPSIS_TEXT_STYLE, color: palette.secondaryText, fontSize: 11 }}>
              {formatTimeRange()}
            </span>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {displayInfo && showAvatars && (
                <EventStatusIcon status={displayInfo.status} />
              )}
              {frozenIcon}
              <span
                style={{
                  ...ELLIPSIS_TEXT_STYLE,
                  fontWeight: 500,
                  fontSize: 12,
                  flex: 1,
                  minWidth: 0,
                  textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
                }}
              >
                {event.customer?.displayName}
              </span>
              {showAvatars && (
                <div style={{ flexShrink: 0 }}>
                  {avatarNode}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
              {displayInfo?.warning && (
                <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#fa8c16', fontSize: 12, flexShrink: 0 }} />
              )}
              <span
                style={{
                  ...ELLIPSIS_TEXT_STYLE,
                  color: palette.secondaryText,
                  fontSize: 12,
                  textDecoration: displayInfo?.areAllWorkAreasComplete ? 'line-through' : undefined,
                }}
              >
                {event.jobName}
              </span>
            </div>
            <span style={{ ...ELLIPSIS_TEXT_STYLE, color: palette.secondaryText, fontSize: 11 }}>
              {formatTimeRange()}
            </span>
          </>
        )}

        {(isHovering || isResizing) && onResize && (
          <WeekViewCardResizeHandle
            setNodeRef={setResizeNodeRef}
            listeners={resizeListeners}
            attributes={resizeAttributes}
            color={palette.primaryText}
          />
        )}
      </div>
    </Popover>
  )
}

export default WeekViewEvent
